import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, X } from "lucide-react";
import { Input } from "../ui/input";
import { useAddAdditionalOffer, useEditAdditionalOffer } from "@/hooks/useLocalStorage";
import { AdditionalOfferSchema, AddAdditionalOfferSchema, AdditionalOfferForm, AddAdditionalAddOfferForm } from "@/shared/partnerschema";

interface AddAdditionalOfferProps {
    open: boolean;
    onClose: () => void;
    data?: AdditionalOfferForm | null;
    offerId?: number;
    onMutationResult: (success: boolean, message: string) => void;
}

function AddAdditionalOffer({ open, onClose, data, offerId, onMutationResult }: AddAdditionalOfferProps) {
    if (!open) return null;

    const addMutation = useAddAdditionalOffer();
    const editMutation = useEditAdditionalOffer();

    const isAddLoading = addMutation.isPending || editMutation.isPending;

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<AddAdditionalAddOfferForm | AdditionalOfferForm>({
        resolver: zodResolver(data ? AdditionalOfferSchema : AddAdditionalOfferSchema),
        defaultValues: data
            ? {
                Id: data.Id,
                ExchangeOfferName: data.ExchangeOfferName,
                AdditionalOfferDetails: data.AdditionalOfferDetails,
            }
            : {
                ExchangeOfferName: "",
                AdditionalOfferDetails: "",
            },
    });

    const onSubmit = async (formData: AddAdditionalAddOfferForm) => {
        if (offerId == null) return;

        if (data?.Id) {
            editMutation.mutate(
                { offerId, additionalOfferId: data.Id, data: formData as AddAdditionalAddOfferForm },
                {
                    onSuccess: () => onMutationResult(true, "Additional offer updated successfully."),
                    onError: (err: any) =>
                        onMutationResult(false, err?.message ?? "Failed to update additional offer."),
                }
            );
        } else {
            addMutation.mutate(
                { offerId, data: formData as AddAdditionalAddOfferForm },
                {
                    onSuccess: () => onMutationResult(true, "Additional offer added successfully."),
                    onError: (err: any) =>
                        onMutationResult(false, err?.message ?? "Failed to add additional offer."),
                }
            );
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white w-full max-w-2xl rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col">

                <div className="flex justify-between items-center px-5 py-3 border-b">
                    <h2 className="text-xl font-bold">
                        {data ? "Edit Additional Offer" : "Add Additional Offer"}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="hover:bg-gray-100 rounded-full p-1 transition-colors"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="flex flex-col flex-1 overflow-hidden">
                    <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Additional Offer Name <span className="text-red-600">*</span>
                        </label>
                        <Input
                            placeholder="Enter additional offer name"
                            required
                            {...register("ExchangeOfferName")}
                        // error={errors.ExchangeOfferName?.message}
                        />

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Additional Offer Details
                            </label>
                            <textarea
                                {...register("AdditionalOfferDetails")}
                                rows={4}
                                placeholder="Enter additional offer details"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {errors.AdditionalOfferDetails && (
                                <p className="text-red-500 text-sm mt-1">
                                    {(errors as any).AdditionalOfferDetails?.message}
                                </p>
                            )}
                        </div>

                    </div>

                    <div className="border-t py-3 px-6 bg-gray-50 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={handleSubmit(onSubmit)}
                            disabled={isAddLoading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isAddLoading ? (
                                <span className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    {data ? "Updating..." : "Adding..."}
                                </span>
                            ) : (
                                data ? "Update Additional Offer" : "Add Additional Offer"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddAdditionalOffer;